/* eslint-disable @typescript-eslint/no-explicit-any */
import api from "@/api";
import { ConfigurationPackage } from "@/api/products";
import { requestScheduleActivation } from "@/api/sale/scheduleActivation";
import { requestUpdatePaymentDataWithoutFormat } from "@/api/sale/updatePaymentData";
import { usePaymentInfoData } from "@/app/payment-data/usePaymentInfoData";
import useTabsValidation from "@/hooks/useTabsValidation";
import useVerifySMSConfirmationIsOn from "@/hooks/useVerifySMSConfimationIsOn";
import { useActiveStore } from "@/store/activePages";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import { convertBRToUSDate, getFidelityByDDD, getOnlyNumbers } from "@/utils";
import { formatPrice } from "@/utils/currency";
import { calculateIdealDueDate } from "@/utils/idealDueDates";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type PriceProductType = {
  ddd: string;
  preco_anterior: number;
  preco_atual: number;
};

type LoyaltyProductType = {
  ativo: number;
  descricao: string;
  descricao_omega: string;
  id: string;
  nome: string;
  oferta_especial: string;
  precos: PriceProductType[];
  prioridade: number;
  requer_aprovacao: number;
  tim_id: string;
};

type ProductType = {
  tim_id: string;
  fidelidades: LoyaltyProductType[];
  pacote_configuracoes_id_substituto?: number;
  pacote_configuracoes: ConfigurationPackage;
};

type DataType = {
  uuid: string;
  cpf: string;
  email: string;
  motherName: string;
  tel: string;
  forma_pagamento: string;
  msisdn: string;
  autorizar_receber_contato: string;
  nascimento: string;
  nome: string;
  product: ProductType;
  telefone: string;
  tipo_fatura: string;
  tipo: string;
  possui_provisorio: string;
  vencimento: string;
  uf: string;
  localidade: string;
  cep: string;
  logradouro: string;
  bairro: string;
  numero: string;
  complemento: string;
  ponto_referencia?: string;
  autorizarContato: string;
  loyalty: boolean;
  birthDate: string;
  fullName: string;
  possuiProvisorio: string;
  utm_campaign: string;
  utm_medium: string;
  utm_source: string;
  userPrefs: string;
};

type PageConfig = {
  route: string;
  prevRoute: string;
  nextRoute: string;
};

// Page Config
export const pageConfigDefault = {
  route: "/short-flow-finish-order",
  prevRoute: "/personal-data",
  nextRoute: "/short-flow-sms-confirmation",
};

export default function useShortFlowFinishOrder() {
  const {
    performEligibilityHandlingWithReturn,
    performAnalysisHandlingWithReturn,
    isExpress,
    tokenizeM4UCreditCardSavingToken,
    setActivateExpressLoading,
    requestActivateSale,
    verifyIfIsNotAuthorizedTransaction,
    payment,
    showForceChangePaymentMethod,
    setShowForceChangePaymentMethod,
    setUserSaidThatDontWantToChange,
    handleChangePaymentMethod,
    method,
    setFormPaymentIsValid,
    formPaymentIsValid,
    paymentDataSchema,
    isLoading,
    changePaymentMethod,
    payments,
    setShouldHideAutomaticDebitAndBilling,
    showUnauthorizedAlert,
    userSaidThatDontWantToChange,
    setShowUnauthorizedAlert,
  } = usePaymentInfoData();

  const [pageConfig, setPageConfig] = useState<PageConfig>(pageConfigDefault);

  const [nextRoute, setNextRoute] = useState(pageConfig.nextRoute);

  useTabsValidation(pageConfig.route, pageConfig.prevRoute);
  const [errorCreateOrder, setErrorCreateOrder] = useState(false);
  const [progressStep, setProgressStep] = useState("");

  const form = useFormStore((store) => store.value);

  const addData = useFormStore((store) => store.addData);

  const setSaleId = useFormStore((store) => store.setSaleId);
  const saleId = useFormStore((store) => store.saleId);
  const setSaleBody = useFormStore((store) => store.setSaleBody);
  const saleBody = useFormStore((store) => store.saleBody);

  const addPage = useActiveStore((store) => store.addPage);

  const product = useProductStore((store) => store.item);

  const loyalty = useProductStore((store) => store.loyalty);

  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );

  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());

  const navigate = useNavigate();

  const timeout = Math.floor(Math.random() * (4 - 1) + 1);

  const handleBackHome = () => {
    navigate("/");
  };

  const isPortability = useFormStore((store) => store.isPortability);
  const isMigration = useFormStore((store) => store.isMigration);
  const phoneNumber = useFormStore((store) => store.value.tel);
  const ddd = phoneNumber.slice(0, 2);

  const smsFromPortabilityIsOn = useVerifySMSConfirmationIsOn(
    ddd,
    isPortability()
  );
  const validationStepIsOn = isMigration() ? true : smsFromPortabilityIsOn;

  const formatAddress = (data: any) => {
    return {
      address: {
        stateCode: data.uf,
        cityName: data.localidade,
        zipCode: data.cep,
        street: data.logradouro,
        neighborhood: data.bairro,
        number: data.numero,
        complement: data.complemento ?? null,
        referencePoint: data.ponto_referencia ?? "",
        noNumber: !data.numero || data.numero === "0",
      },
    };
  };

  const formatPayload = useCallback(
    (data: DataType, formaPagamento = "Fatura") => {
      let fidelidadeTIMId = {};

      if (data.product?.fidelidades) {
        fidelidadeTIMId = {
          fidelidade: getFidelityByDDD(
            data.product.fidelidades,
            String(getOnlyNumbers(data.tel).slice(0, 2))
          )?.tim_id,
        };
      }

      const loyaltyData = loyalty ? fidelidadeTIMId : {};

      window.nstim?.validate("user_prefs");

      const user_prefs = (document.getElementById("user_prefs") as HTMLInputElement)?.value;

      const user_prefs2 = (document.getElementById("user_prefs2") as HTMLInputElement)?.value;

      const userPrefs = {
        user_prefs,
        user_prefs2,
      };
      const configurationPackage = data.product
        .pacote_configuracoes_id_substituto
        ? {
            pacote_configuracoes_id_substituto:
              data.product.pacote_configuracoes_id_substituto,
          }
        : {};
      return {
        uuid: data.uuid,
        cpf: getOnlyNumbers(data.cpf),
        cliente_cpf: getOnlyNumbers(data.cpf),
        email: data.email,
        filiacao: data.motherName,
        forma_pagamento: formaPagamento,
        msisdn: getOnlyNumbers(data.tel),
        autorizar_receber_contato: data.autorizarContato,
        nascimento: convertBRToUSDate(data.birthDate),
        nome: data.fullName,
        plano: data.product.tim_id,
        telefone: getOnlyNumbers(data.tel),
        tipo_fatura: "Conta Online",
        tipo: data.tipo,
        possui_provisorio: data.possuiProvisorio,
        vencimento: calculateIdealDueDate(new Date().getDay()),
        uf: data.uf,
        cidade: data.localidade,
        cep: getOnlyNumbers(data.cep),
        logradouro: data.logradouro,
        bairro: data.bairro,
        numero: data.numero,
        complemento: data.complemento ?? null,
        ponto_referencia: data.ponto_referencia ?? null,
        utm_campaign: data.utm_campaign,
        utm_medium: data.utm_medium,
        utm_source: data.utm_source,
        userPrefs,
        ...loyaltyData,
        ...configurationPackage,
      };
    },
    [loyalty]
  );

  const sendGAAddress = useCallback(() => {
    window?.dataLayer?.push({
      event: "step",
      passo: "ENDERECO",
      sku: removeAccentsAndSetUpperCase(product?.tim_id),
      nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
        product?.nome
      )}-${totalGbProduct}`,
      valor: formatPrice(currentPrice),
      status: "Sucesso",
    });
  }, [currentPrice, product?.nome, product?.tim_id, totalGbProduct]);

  const sendGAPayment = useCallback(() => {
    window?.dataLayer?.push({
      event: "step",
      passo: "PAGAMENTO",
      sku: removeAccentsAndSetUpperCase(product?.tim_id),
      nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
        product?.nome
      )}-${totalGbProduct}`,
      valor: formatPrice(currentPrice),
      status: "Sucesso",
    });
  }, [currentPrice, product?.nome, product?.tim_id, totalGbProduct]);

  const buscaEnderecoValido = useCallback(async () => {
    const _cpf = form?.cpf?.replace(/[^0-9]+/g, "");
    const _msisdn = form?.tel?.replace(/[^0-9]+/g, "");

    if (!_cpf || !_msisdn) {
      return;
    }

    const MAX_RETRIES = 3;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const { data } = await api.post(
          "/integrations/dados-pessoais/buscar-endereco-valido",
          {
            cpf: _cpf,
            msisdn: _msisdn,
          }
        );

        return data;
      } catch (e) {
        console.error(`[buscaEnderecoValido] tentativa ${attempt}/${MAX_RETRIES} falhou`, e);
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }

    console.error("[buscaEnderecoValido] todas as tentativas falharam");
  }, [form]);

  const createSale = useCallback(
    async (enderecoValido: any) => {
      const {
        data: { id },
      } = await api.post("/core/vendas", enderecoValido);
      setSaleId(id);
      setSaleBody({
        ...enderecoValido.sale_form,
        venda_id: id,
      });
      return id;
    },
    [setSaleBody, setSaleId]
  );

  const handleShortChangePaymentMethod = async () => {
    // Limpa erro residual setado pelo createSaleOrder do mount: o cliente
    // esta optando por trocar de pagamento, entao a tela passa a mostrar
    // o form de cartao e nao deve carregar a mensagem generica abaixo.
    setErrorCreateOrder(false);
    changePaymentMethod(payments.cartaodecredito);
    setShouldHideAutomaticDebitAndBilling(true);
    setNextRoute("/confirmation/auto-debt");
  };

  const createSaleOrder = async () => {
    try {
      setProgressStep("Buscando endereço...");
      const enderecoValido = await buscaEnderecoValido();

      if (!enderecoValido) {
        console.error("[createSaleOrder] endereço não encontrado, abortando criação da venda");
        setErrorCreateOrder(true);
        setProgressStep("");
        return;
      }

      const tempPayload = {
        ...form,
        ...enderecoValido,
        product: product!,
      };
      const form_data = { sale_form: formatPayload(tempPayload) };

      addData({
        ...form,
        ...formatAddress(enderecoValido),
        paymentType: { value: form_data.sale_form.forma_pagamento },
        payment: { debtDueDate: form_data.sale_form.vencimento },
      });
      setProgressStep("Criando pedido...");
      const saleID = await createSale(form_data);
      requestScheduleActivation(saleID).catch((err) =>
        console.error("[requestScheduleActivation]", err)
      );

      setProgressStep("Verificando elegibilidade...");
      await performEligibilityHandlingWithReturn(saleID);
      setProgressStep("Realizando análise de segurança...");
      await performAnalysisHandlingWithReturn(saleID);

      navigate(pageConfig.nextRoute);
      addPage(nextRoute);
    } catch (err) {
      console.error("[createSaleOrder]", err);
      setErrorCreateOrder(true);
      setProgressStep("");
    }
  };

  const createExpressSaleOrder = async (payment: any) => {
    if (!isExpress()) {
      return createSaleOrder();
    }
    await requestUpdatePaymentDataWithoutFormat({
      ...saleBody,
      forma_pagamento: "Cartão de Crédito",
      plano: product?.tim_id,
    });

    addData(payment);

    try {
      await tokenizeM4UCreditCardSavingToken(
        form.cpf,
        saleId!,
        payment.payment
      );
      setActivateExpressLoading(true);
      await requestActivateSale({
        venda_id: saleId!,
        cliente_cpf: form.cpf,
      }).finally(() => setActivateExpressLoading(false));

      navigate(nextRoute);
      addPage(nextRoute);
    } catch (err) {
      verifyIfIsNotAuthorizedTransaction(err);
    }
  };

  useEffect(() => {
    createSaleOrder();

    setTimeout(() => {
      sendGAAddress();
      sendGAPayment();
    }, timeout);
    // Executa apenas no mount: cria a venda automaticamente ao entrar na pagina
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userSaidThatDontWantToChange && saleId) {
      navigate("/confirmation/auto-debt");
      addPage("/confirmation/auto-debt");

      setPageConfig((prev) => ({
        ...prev,
        nextRoute: "/confirmation/auto-debt",
      }));
    }
    // Reage apenas a mudanca de pagamento recusada + saleId; demais deps sao refs estaveis
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSaidThatDontWantToChange, saleId]);

  useEffect(() => {
    let route = pageConfig.nextRoute;

    if (userSaidThatDontWantToChange || !validationStepIsOn) {
      route = "/confirmation/auto-debt";
    }

    setNextRoute(route);
  }, [pageConfig.nextRoute, userSaidThatDontWantToChange, validationStepIsOn]);

  return {
    errorCreateOrder,
    handleBackHome,
    showForceChangePaymentMethod,
    setShowForceChangePaymentMethod,
    setUserSaidThatDontWantToChange,
    handleChangePaymentMethod,
    method,
    payment,
    setFormPaymentIsValid,
    handleShortChangePaymentMethod,
    pageConfig,
    formPaymentIsValid,
    createSaleOrder,
    paymentDataSchema,
    createExpressSaleOrder,
    isLoading,
    progressStep,
    showUnauthorizedAlert,
    setShowUnauthorizedAlert,
  };
}
